import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Tabs, Tab, Box, CircularProgress, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Stack, IconButton, TableSortLabel
} from '@mui/material';
import { getAthleteSkis, getAthleteTests, getAthleteRaces, addAthleteSki, updateAthleteSki, deleteAthleteSki, bulkAddAthleteSkis } from '../api';
import InfoIcon from '@mui/icons-material/Info';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { SkiDetailModal } from './SkisTable';
import { TestDetailModal } from './TestsTable';
import { RaceDetailModal } from './RacesTable';
import SkiSpreadsheetUpload from '../components/SkiSpreadsheetUpload';
import { SkiForm } from './SkisSection';

export default function AthleteReadOnlyView({ open, onClose, athlete, team }) {
  const [tab, setTab] = useState(0);
  const [skis, setSkis] = useState(null);
  const [tests, setTests] = useState(null);
  const [races, setRaces] = useState(null);
  const [athleteInfo, setAthleteInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [skiDetail, setSkiDetail] = useState(null);
  const [testDetail, setTestDetail] = useState(null);
  const [raceDetail, setRaceDetail] = useState(null);
  const [skiModal, setSkiModal] = useState({ open: false, ski: null });
  const [uploadOpen, setUploadOpen] = useState(false);
  
  // Separate sorting state for each brand
  const [sortStates, setSortStates] = useState({});

  const fetchData = useCallback(() => {
    if (open && athlete && team) {
      setLoading(true);
      Promise.all([
        getAthleteSkis(team.id, athlete.id),
        getAthleteTests(team.id, athlete.id),
        getAthleteRaces(team.id, athlete.id)
      ]).then(([skisResponse, tests, races]) => {
        // Handle new response format with athleteInfo
        if (skisResponse && skisResponse.skis) {
          setSkis(skisResponse.skis);
          setAthleteInfo(skisResponse.athleteInfo);
        } else {
          // Fallback for old format
          setSkis(skisResponse);
          setAthleteInfo({ weight_kg: null, height_cm: null });
        }
        setTests(tests);
        setRaces(races);
        setLoading(false);
      }).catch(err => {
        console.error('Error fetching athlete data:', err);
        setLoading(false);
      });
    }
  }, [open, athlete, team]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSkiSave = async (skiData) => {
    if (skiModal.ski) {
      // Update existing ski
      await updateAthleteSki(team.id, athlete.id, skiModal.ski.id, skiData);
    } else {
      // Add new ski
      await addAthleteSki(team.id, athlete.id, skiData);
    }
    fetchData();
  };

  const handleSkiDelete = async (skiId) => {
    if (window.confirm('Are you sure you want to delete this ski?')) {
      await deleteAthleteSki(team.id, athlete.id, skiId);
      fetchData();
    }
  };

  const handleBulkUpload = async (result) => {
    // result is the response from bulkAddAthleteSkis
    fetchData();
  };

  const handleTabChange = (e, newValue) => setTab(newValue);

  const handleSort = (brand, property) => {
    const currentState = sortStates[brand] || { orderBy: 'ski_number', order: 'asc' };
    const isAsc = currentState.orderBy === property && currentState.order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    
    setSortStates(prev => ({
      ...prev,
      [brand]: { orderBy: property, order: newOrder }
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Viewing as {athlete?.name}</DialogTitle>
      <DialogContent dividers>
        <Tabs value={tab} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label="Skis" />
          <Tab label="Tests" />
          <Tab label="Races" />
        </Tabs>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {tab === 0 && (
              <Box>
                {/* Athlete Stats */}
                {athleteInfo && (athleteInfo.weight_kg || athleteInfo.height_cm) && (
                  <Paper sx={{ p: 2, mb: 3, backgroundColor: 'primary.light', color: 'primary.contrastText' }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>Athlete Information</Typography>
                    <Stack direction="row" spacing={3}>
                      {athleteInfo.weight_kg && (
                        <Typography variant="body1">
                          <strong>Weight:</strong> {athleteInfo.weight_kg} kg
                        </Typography>
                      )}
                      {athleteInfo.height_cm && (
                        <Typography variant="body1">
                          <strong>Height:</strong> {athleteInfo.height_cm} cm
                        </Typography>
                      )}
                    </Stack>
                  </Paper>
                )}
                
                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setSkiModal({ open: true, ski: null })}
                  >
                    Add Ski
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<UploadFileIcon />}
                    onClick={() => setUploadOpen(true)}
                  >
                    Upload Spreadsheet
                  </Button>
                </Box>
                
                <Typography variant="h6" sx={{ mb: 1 }}>Classic Skis</Typography>
                {(() => {
                  const classicSkis = skis ? skis.filter(s => s.type === 'classic') : [];
                  const groupedSkis = {};
                  classicSkis.forEach(ski => {
                    if (!groupedSkis[ski.brand]) {
                      groupedSkis[ski.brand] = [];
                    }
                    groupedSkis[ski.brand].push(ski);
                  });
                  
                  // Sort brands by number of skis (most to least)
                  const sortedBrands = Object.entries(groupedSkis).sort(([, a], [, b]) => b.length - a.length);
                  
                  return sortedBrands.map(([brand, brandSkis]) => {
                    const isFischer = brand === 'Fischer';
                    const isSalomonAtomic = ['Salomon', 'Atomic'].includes(brand);
                    const isRossignol = brand === 'Rossignol';
                    
                    // Get sorting state for this brand
                    const brandSortState = sortStates[brand] || { orderBy: 'ski_number', order: 'asc' };
                    
                    // Sort skis within this brand table
                    const sortedBrandSkis = [...brandSkis].sort((a, b) => {
                      if (brandSortState.orderBy === 'fa' || brandSortState.orderBy === 'hr' || brandSortState.orderBy === 'mf' || brandSortState.orderBy === 'h4') {
                        const aVal = parseFloat(a[brandSortState.orderBy]) || 0;
                        const bVal = parseFloat(b[brandSortState.orderBy]) || 0;
                        return brandSortState.order === 'asc' ? aVal - bVal : bVal - aVal;
                      }
                      // Default: ski_number - handle as string for natural sorting
                      const aNum = parseInt(a.ski_number) || 0;
                      const bNum = parseInt(b.ski_number) || 0;
                      return brandSortState.order === 'asc' ? aNum - bNum : bNum - aNum;
                    });
                    
                    return (
                      <Box key={brand} sx={{ mb: 4 }}>
                        <Typography variant="subtitle1" sx={{ mb: 2, color: 'primary.main' }}>
                          {brand} Skis ({brandSkis.length})
                        </Typography>
                        <TableContainer component={Paper} sx={{ mb: 4, borderRadius: 2, boxShadow: 2, width: '100%', minWidth: 600 }}>
                          <Table sx={{ width: '100%' }}>
                            <TableHead>
                              <TableRow sx={{ background: '#f5f5f5' }}>
                                <TableCell>Ski #</TableCell>
                                {isFischer && (
                                  <>
                                    <TableCell sortDirection={brandSortState.orderBy === 'fa' ? brandSortState.order : false}>
                                      <TableSortLabel
                                        active={brandSortState.orderBy === 'fa'}
                                        direction={brandSortState.orderBy === 'fa' ? brandSortState.order : 'asc'}
                                        onClick={() => handleSort(brand, 'fa')}
                                      >
                                        FA
                                      </TableSortLabel>
                                    </TableCell>
                                    <TableCell sortDirection={brandSortState.orderBy === 'hr' ? brandSortState.order : false}>
                                      <TableSortLabel
                                        active={brandSortState.orderBy === 'hr'}
                                        direction={brandSortState.orderBy === 'hr' ? brandSortState.order : 'asc'}
                                        onClick={() => handleSort(brand, 'hr')}
                                      >
                                        HR
                                      </TableSortLabel>
                                    </TableCell>
                                  </>
                                )}
                                {isSalomonAtomic && (
                                  <>
                                    <TableCell sortDirection={brandSortState.orderBy === 'mf' ? brandSortState.order : false}>
                                      <TableSortLabel
                                        active={brandSortState.orderBy === 'mf'}
                                        direction={brandSortState.orderBy === 'mf' ? brandSortState.order : 'asc'}
                                        onClick={() => handleSort(brand, 'mf')}
                                      >
                                        MF
                                      </TableSortLabel>
                                    </TableCell>
                                    <TableCell sortDirection={brandSortState.orderBy === 'h4' ? brandSortState.order : false}>
                                      <TableSortLabel
                                        active={brandSortState.orderBy === 'h4'}
                                        direction={brandSortState.orderBy === 'h4' ? brandSortState.order : 'asc'}
                                        onClick={() => handleSort(brand, 'h4')}
                                      >
                                        H4
                                      </TableSortLabel>
                                    </TableCell>
                                  </>
                                )}
                                {isRossignol && (
                                  <>
                                    <TableCell sortDirection={brandSortState.orderBy === 'rossi_flex' ? brandSortState.order : false}>
                                      <TableSortLabel
                                        active={brandSortState.orderBy === 'rossi_flex'}
                                        direction={brandSortState.orderBy === 'rossi_flex' ? brandSortState.order : 'asc'}
                                        onClick={() => handleSort(brand, 'rossi_flex')}
                                      >
                                        Flex
                                      </TableSortLabel>
                                    </TableCell>
                                    <TableCell sortDirection={brandSortState.orderBy === 'rossi_height' ? brandSortState.order : false}>
                                      <TableSortLabel
                                        active={brandSortState.orderBy === 'rossi_height'}
                                        direction={brandSortState.orderBy === 'rossi_height' ? brandSortState.order : 'asc'}
                                        onClick={() => handleSort(brand, 'rossi_height')}
                                      >
                                        Height
                                      </TableSortLabel>
                                    </TableCell>
                                    <TableCell sortDirection={brandSortState.orderBy === 'mold' ? brandSortState.order : false}>
                                        <TableSortLabel
                                            active={brandSortState.orderBy === 'mold'}
                                            direction={brandSortState.orderBy === 'mold' ? brandSortState.order : 'asc'}
                                            onClick={() => handleSort(brand, 'mold')}
                                      >
                                        Mold
                                      </TableSortLabel>
                                    </TableCell>
                                  </>
                                )}
                                <TableCell>Grind</TableCell>
                                <TableCell>Base</TableCell>
                                <TableCell>Year</TableCell>
                                <TableCell align="center">Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {sortedBrandSkis.map((ski) => (
                                <TableRow key={ski.id} hover>
                                  <TableCell>{ski.ski_number}</TableCell>
                                  {isFischer && (
                                    <>
                                      <TableCell>{ski.fa}</TableCell>
                                      <TableCell>{ski.hr}</TableCell>
                                    </>
                                  )}
                                  {isSalomonAtomic && (
                                    <>
                                      <TableCell>{ski.mf}</TableCell>
                                      <TableCell>{ski.h4}</TableCell>
                                    </>
                                  )}
                                  {isRossignol && (
                                    <>
                                      <TableCell>{ski.rossi_flex}</TableCell>
                                      <TableCell>{ski.rossi_height}</TableCell>
                                    </>
                                  )}
                                  <TableCell>{ski.grind}</TableCell>
                                  <TableCell>{ski.base}</TableCell>
                                  <TableCell>{ski.year_made}</TableCell>
                                  <TableCell align="center">
                                    <Stack direction="row" spacing={1} justifyContent="center">
                                      <IconButton size="small" onClick={() => setSkiDetail(ski)}><InfoIcon /></IconButton>
                                      <IconButton size="small" onClick={() => setSkiModal({ open: true, ski })}><EditIcon /></IconButton>
                                      <IconButton size="small" onClick={() => handleSkiDelete(ski.id)} color="error"><DeleteIcon /></IconButton>
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    );
                  });
                })()}
                
                <Typography variant="h6" sx={{ mb: 1, mt: 3 }}>Skate Skis</Typography>
                {(() => {
                  const skateSkis = skis ? skis.filter(s => s.type === 'skate') : [];
                  const groupedSkis = {};
                  skateSkis.forEach(ski => {
                    if (!groupedSkis[ski.brand]) {
                      groupedSkis[ski.brand] = [];
                    }
                    groupedSkis[ski.brand].push(ski);
                  });
                  
                  // Sort brands by number of skis (most to least)
                  const sortedBrands = Object.entries(groupedSkis).sort(([, a], [, b]) => b.length - a.length);
                  
                  return sortedBrands.map(([brand, brandSkis]) => {
                    const isFischer = brand === 'Fischer';
                    const isSalomonAtomic = ['Salomon', 'Atomic'].includes(brand);
                    const isRossignol = brand === 'Rossignol';
                    
                    // Get sorting state for this brand
                    const brandSortState = sortStates[brand] || { orderBy: 'ski_number', order: 'asc' };
                    
                    // Sort skis within this brand table
                    const sortedBrandSkis = [...brandSkis].sort((a, b) => {
                      if (brandSortState.orderBy === 'fa' || brandSortState.orderBy === 'hr' || brandSortState.orderBy === 'mf' || brandSortState.orderBy === 'h4') {
                        const aVal = parseFloat(a[brandSortState.orderBy]) || 0;
                        const bVal = parseFloat(b[brandSortState.orderBy]) || 0;
                        return brandSortState.order === 'asc' ? aVal - bVal : bVal - aVal;
                      }
                      // Default: ski_number - handle as string for natural sorting
                      const aNum = parseInt(a.ski_number) || 0;
                      const bNum = parseInt(b.ski_number) || 0;
                      return brandSortState.order === 'asc' ? aNum - bNum : bNum - aNum;
                    });
                    
                    return (
                      <Box key={brand} sx={{ mb: 4 }}>
                        <Typography variant="subtitle1" sx={{ mb: 2, color: 'primary.main' }}>
                          {brand} Skis ({brandSkis.length})
                        </Typography>
                        <TableContainer component={Paper} sx={{ mb: 4, borderRadius: 2, boxShadow: 2, width: '100%', minWidth: 600 }}>
                          <Table sx={{ width: '100%' }}>
                            <TableHead>
                              <TableRow sx={{ background: '#f5f5f5' }}>
                                <TableCell>Ski #</TableCell>
                                {isFischer && (
                                  <>
                                    <TableCell sortDirection={brandSortState.orderBy === 'fa' ? brandSortState.order : false}>
                                      <TableSortLabel
                                        active={brandSortState.orderBy === 'fa'}
                                        direction={brandSortState.orderBy === 'fa' ? brandSortState.order : 'asc'}
                                        onClick={() => handleSort(brand, 'fa')}
                                      >
                                        FA
                                      </TableSortLabel>
                                    </TableCell>
                                    <TableCell sortDirection={brandSortState.orderBy === 'hr' ? brandSortState.order : false}>
                                      <TableSortLabel
                                        active={brandSortState.orderBy === 'hr'}
                                        direction={brandSortState.orderBy === 'hr' ? brandSortState.order : 'asc'}
                                        onClick={() => handleSort(brand, 'hr')}
                                      >
                                        HR
                                      </TableSortLabel>
                                    </TableCell>
                                  </>
                                )}
                                {isSalomonAtomic && (
                                  <>
                                    <TableCell sortDirection={brandSortState.orderBy === 'mf' ? brandSortState.order : false}>
                                      <TableSortLabel
                                        active={brandSortState.orderBy === 'mf'}
                                        direction={brandSortState.orderBy === 'mf' ? brandSortState.order : 'asc'}
                                        onClick={() => handleSort(brand, 'mf')}
                                      >
                                        MF
                                      </TableSortLabel>
                                    </TableCell>
                                    <TableCell sortDirection={brandSortState.orderBy === 'h4' ? brandSortState.order : false}>
                                      <TableSortLabel
                                        active={brandSortState.orderBy === 'h4'}
                                        direction={brandSortState.orderBy === 'h4' ? brandSortState.order : 'asc'}
                                        onClick={() => handleSort(brand, 'h4')}
                                      >
                                        H4
                                      </TableSortLabel>
                                    </TableCell>
                                  </>
                                )}
                                {isRossignol && (
                                  <>
                                    <TableCell sortDirection={brandSortState.orderBy === 'rossi_flex' ? brandSortState.order : false}>
                                      <TableSortLabel
                                        active={brandSortState.orderBy === 'rossi_flex'}
                                        direction={brandSortState.orderBy === 'rossi_flex' ? brandSortState.order : 'asc'}
                                        onClick={() => handleSort(brand, 'rossi_flex')}
                                      >
                                        Flex
                                      </TableSortLabel>
                                    </TableCell>
                                    <TableCell sortDirection={brandSortState.orderBy === 'rossi_height' ? brandSortState.order : false}>
                                      <TableSortLabel
                                        active={brandSortState.orderBy === 'rossi_height'}
                                        direction={brandSortState.orderBy === 'rossi_height' ? brandSortState.order : 'asc'}
                                        onClick={() => handleSort(brand, 'rossi_height')}
                                      >
                                        Height
                                      </TableSortLabel>
                                    </TableCell>
                                    <TableCell sortDirection={brandSortState.orderBy === 'mold' ? brandSortState.order : false}>
                                        <TableSortLabel
                                            active={brandSortState.orderBy === 'mold'}
                                            direction={brandSortState.orderBy === 'mold' ? brandSortState.order : 'asc'}
                                            onClick={() => handleSort(brand, 'mold')}
                                      >
                                        Mold
                                      </TableSortLabel>
                                    </TableCell>
                                  </>
                                )}
                                <TableCell>Grind</TableCell>
                                <TableCell>Base</TableCell>
                                <TableCell>Year</TableCell>
                                <TableCell align="center">Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {sortedBrandSkis.map((ski) => (
                                <TableRow key={ski.id} hover>
                                  <TableCell>{ski.ski_number}</TableCell>
                                  {isFischer && (
                                    <>
                                      <TableCell>{ski.fa}</TableCell>
                                      <TableCell>{ski.hr}</TableCell>
                                    </>
                                  )}
                                  {isSalomonAtomic && (
                                    <>
                                      <TableCell>{ski.mf}</TableCell>
                                      <TableCell>{ski.h4}</TableCell>
                                    </>
                                  )}
                                  {isRossignol && (
                                    <>
                                      <TableCell>{ski.rossi_flex}</TableCell>
                                      <TableCell>{ski.rossi_height}</TableCell>
                                      <TableCell>{ski.mold}</TableCell>
                                    </>
                                  )}
                                  <TableCell>{ski.grind}</TableCell>
                                  <TableCell>{ski.base}</TableCell>
                                  <TableCell>{ski.year_made}</TableCell>
                                  <TableCell align="center">
                                    <Stack direction="row" spacing={1} justifyContent="center">
                                      <IconButton size="small" onClick={() => setSkiDetail(ski)}><InfoIcon /></IconButton>
                                      <IconButton size="small" onClick={() => setSkiModal({ open: true, ski })}><EditIcon /></IconButton>
                                      <IconButton size="small" onClick={() => handleSkiDelete(ski.id)} color="error"><DeleteIcon /></IconButton>
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    );
                  });
                })()}
                <SkiDetailModal open={!!skiDetail} onClose={() => setSkiDetail(null)} ski={skiDetail} />
                {/* Ski Form Modal - using SkiForm from SkisSection */}
                <SkiForm 
                  open={skiModal.open} 
                  onClose={() => setSkiModal({ open: false, ski: null })} 
                  onSave={handleSkiSave} 
                  initial={skiModal.ski}
                />
                {/* Spreadsheet Upload - using modified version for coaches */}
                <SkiSpreadsheetUpload
                  open={uploadOpen}
                  onClose={() => {
                    setUploadOpen(false);
                    fetchData();
                  }}
                  onSuccess={handleBulkUpload}
                  bulkAddFunction={async (skisData) => {
                    const result = await bulkAddAthleteSkis(team.id, athlete.id, skisData);
                    return result;
                  }}
                />
              </Box>
            )}
            {tab === 1 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 1 }}>Tests</Typography>
                <TableContainer component={Paper} sx={{ mb: 4, borderRadius: 2, boxShadow: 2, width: '100%', minWidth: 600 }}>
                  <Table sx={{ width: '100%' }}>
                    <TableHead>
                      <TableRow sx={{ background: '#f5f5f5' }}>
                        <TableCell>Name</TableCell>
                        <TableCell>Location</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Technique</TableCell>
                        <TableCell>Ranked Skis</TableCell>
                        <TableCell align="center">Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tests.map((test) => (
                        <TableRow key={test.id} hover>
                          <TableCell>{test.name || ''}</TableCell>
                          <TableCell>{test.location || ''}</TableCell>
                          <TableCell>{test.date ? test.date.split('T')[0] : ''}</TableCell>
                          <TableCell>{test.technique}</TableCell>
                          <TableCell>
                            {test.ski_ids_ranked?.slice(0, 5).map((id, idx) => `#${(skis.find(s => s.id === id)?.ski_number) || id}${idx < Math.min(test.ski_ids_ranked.length, 5) - 1 ? ', ' : ''}`)}
                            {test.ski_ids_ranked?.length > 5 && ' ...'}
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={1} justifyContent="center">
                              <IconButton size="small" onClick={() => setTestDetail(test)}><InfoIcon /></IconButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TestDetailModal open={!!testDetail} onClose={() => setTestDetail(null)} test={testDetail} skis={skis || []} />
              </Box>
            )}
            {tab === 2 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 1 }}>Races</Typography>
                <TableContainer component={Paper} sx={{ mb: 4, borderRadius: 2, boxShadow: 2, width: '100%', minWidth: 600 }}>
                  <Table sx={{ width: '100%' }}>
                    <TableHead>
                      <TableRow sx={{ background: '#f5f5f5' }}>
                        <TableCell>Name</TableCell>
                        <TableCell>Location</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Ski</TableCell>
                        <TableCell>Technique</TableCell>
                        <TableCell>Performance</TableCell>
                        <TableCell>Comments</TableCell>
                        <TableCell align="center">Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {races.map((race) => {
                        const skiMap = Object.fromEntries((skis || []).map(ski => [ski.id, ski.ski_number]));
                        const skiDisplay = race.ski_id ? (skiMap[race.ski_id] ? `#${skiMap[race.ski_id]}` : 'Ski no longer part of fleet') : 'Ski no longer part of fleet';
                        return (
                          <TableRow key={race.id} hover>
                            <TableCell>{race.name}</TableCell>
                            <TableCell>{race.location}</TableCell>
                            <TableCell>{race.date ? race.date.split('T')[0] : ''}</TableCell>
                            <TableCell>{skiDisplay}</TableCell>
                            <TableCell>{race.technique}</TableCell>
                            <TableCell>{race.performance}</TableCell>
                            <TableCell>{race.comments}</TableCell>
                            <TableCell align="center">
                              <Stack direction="row" spacing={1} justifyContent="center">
                                <IconButton size="small" onClick={() => setRaceDetail(race)}><InfoIcon /></IconButton>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <RaceDetailModal open={!!raceDetail} onClose={() => setRaceDetail(null)} race={raceDetail} skis={skis || []} />
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
} 